import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Shield, Store, ChevronDown, ChevronUp, Loader2, LogOut, RefreshCw, CheckCircle2, Save } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FLAG_LABELS = {
  extended_product_limit: { label: 'Extended Product Limit',    desc: 'Bypass plan product cap for custom/enterprise deals' },
  custom_voice_override:  { label: 'Custom Voice Override',     desc: 'Bypass custom voice trial & plan restrictions' },
  full_analytics:         { label: 'Full Analytics',            desc: 'Unlock full analytics regardless of plan tier' },
  bulk_generation:        { label: 'Bulk Generation',           desc: 'Enable bulk audio generation' },
  collection_card_audio:  { label: 'Collection Card Audio',     desc: 'Audio player on product listing/collection cards' },
  dual_product_player:    { label: 'Dual Product Player',       desc: 'Two audio players per product page (different audio per slot)' },
  metadata_audio:         { label: 'Metadata Audio',            desc: 'Generate audio from custom product metafields' },
};

async function adminFetch(path, secret, options = {}) {
  const res = await fetch(`${BACKEND}/api${path}`, {
    ...options,
    headers: { 'X-Admin-Secret': secret, 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
}

function StoreRow({ store, secret, onFlagChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Local staged state — changes here don't hit the API until Save is clicked
  const [pending, setPending] = useState(store.features || {});

  // Detect unsaved changes
  const savedFeatures = store.features || {};
  const hasPendingChanges = Object.keys(FLAG_LABELS).some(
    flag => (pending[flag] ?? false) !== (savedFeatures[flag] ?? false)
  );

  const handleToggle = (flag, newVal) => {
    setPending(p => ({ ...p, [flag]: newVal }));
    setSavedAt(null); // clear saved confirmation when new changes are made
  };

  const handleSave = async () => {
    setSaving(true);
    const changedFlags = Object.keys(FLAG_LABELS).filter(
      flag => (pending[flag] ?? false) !== (savedFeatures[flag] ?? false)
    );
    try {
      await Promise.all(
        changedFlags.map(flag =>
          adminFetch(`/admin/stores/${encodeURIComponent(store.shop_domain)}/features`, secret, {
            method: 'PATCH',
            body: JSON.stringify({ flag, enabled: pending[flag] ?? false }),
          })
        )
      );
      changedFlags.forEach(flag => onFlagChange(store.shop_domain, flag, pending[flag] ?? false));
      const now = new Date();
      setSavedAt(now.toLocaleTimeString());
      toast.success(`Settings saved for ${store.shop_domain}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setPending(store.features || {});
    setSavedAt(null);
  };

  const activeFlags = Object.values(pending).filter(Boolean).length;

  return (
    <div data-testid={`store-row-${store.shop_domain}`} className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-gray-50 transition-colors text-left"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-3">
          <Store className="h-4 w-4 text-gray-400 shrink-0" />
          <span className="text-sm font-medium text-gray-900">{store.shop_domain}</span>
          {store.has_expiring_token && (
            <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">Expiring Token</Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          {hasPendingChanges && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Unsaved changes</Badge>
          )}
          {!hasPendingChanges && savedAt && (
            <Badge className="bg-green-100 text-green-700 border-green-200 text-xs flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Saved {savedAt}
            </Badge>
          )}
          {!hasPendingChanges && !savedAt && activeFlags > 0 && (
            <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs">{activeFlags} flag{activeFlags !== 1 ? 's' : ''} on</Badge>
          )}
          {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 bg-gray-50 px-5 py-4 space-y-4">
          {/* Flag toggles */}
          <div className="space-y-3">
            {Object.entries(FLAG_LABELS).map(([flag, { label, desc }]) => {
              const isChanged = (pending[flag] ?? false) !== (savedFeatures[flag] ?? false);
              return (
                <div
                  key={flag}
                  className={`flex items-center justify-between gap-4 rounded-lg px-3 py-2 transition-colors ${
                    isChanged ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-transparent'
                  }`}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
                      {label}
                      {isChanged && <span className="text-xs text-amber-600 font-normal">(unsaved)</span>}
                    </p>
                    <p className="text-xs text-gray-500">{desc}</p>
                  </div>
                  <Switch
                    data-testid={`flag-toggle-${store.shop_domain}-${flag}`}
                    checked={pending[flag] ?? false}
                    onCheckedChange={val => handleToggle(flag, val)}
                    disabled={saving}
                  />
                </div>
              );
            })}
          </div>

          {/* Save / Discard row */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            {savedAt && !hasPendingChanges ? (
              <span className="flex items-center gap-1.5 text-xs text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                All settings saved at {savedAt}
              </span>
            ) : (
              <span className="text-xs text-gray-400">
                {hasPendingChanges ? 'You have unsaved changes.' : 'No changes pending.'}
              </span>
            )}
            <div className="flex items-center gap-2">
              {hasPendingChanges && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDiscard}
                  disabled={saving}
                  className="text-gray-500 hover:text-gray-700 text-xs"
                >
                  Discard
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!hasPendingChanges || saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1.5"
                data-testid={`save-flags-${store.shop_domain}`}
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const [secret, setSecret] = useState(() => sessionStorage.getItem('admin_secret') || '');
  const [inputSecret, setInputSecret] = useState('');
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);

  const loadStores = useCallback(async (s) => {
    setLoading(true);
    try {
      const data = await adminFetch('/admin/stores', s);
      setStores(data.stores);
    } catch (e) {
      toast.error(e.message === 'Forbidden: invalid admin secret' ? 'Wrong admin secret' : e.message);
      setAuthed(false);
      sessionStorage.removeItem('admin_secret');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await adminFetch('/admin/stores', inputSecret);
      setStores(data.stores);
      setSecret(inputSecret);
      sessionStorage.setItem('admin_secret', inputSecret);
      setAuthed(true);
    } catch (e) {
      toast.error('Wrong admin secret');
    } finally {
      setLoading(false);
    }
  };

  const handleFlagChange = (shopDomain, flag, value) => {
    setStores(prev => prev.map(s =>
      s.shop_domain === shopDomain
        ? { ...s, features: { ...s.features, [flag]: value } }
        : s
    ));
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_secret');
    setAuthed(false);
    setSecret('');
    setStores([]);
  };

  // Auto-login if secret stored in session
  React.useEffect(() => {
    if (secret && !authed) {
      loadStores(secret).then(() => setAuthed(true)).catch(() => {});
    }
  }, []);

  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <Toaster position="top-right" />
        <div className="w-full max-w-sm space-y-6">
          <div className="flex items-center gap-3 justify-center">
            <Shield className="h-8 w-8 text-indigo-400" />
            <h1 className="text-2xl font-bold text-white">Speakify Admin</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              data-testid="admin-secret-input"
              type="password"
              placeholder="Enter admin secret"
              value={inputSecret}
              onChange={e => setInputSecret(e.target.value)}
              className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-indigo-500"
            />
            <Button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading || !inputSecret}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const activeStores = stores.filter(s => s.has_expiring_token !== undefined);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Toaster position="top-right" />
      {/* Top bar */}
      <div className="border-b border-gray-800 bg-gray-900 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-indigo-400" />
          <span className="font-semibold text-white">Speakify Admin</span>
          <Badge className="bg-indigo-900 text-indigo-300 border-indigo-700 text-xs">{stores.length} stores</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="admin-refresh-btn"
            variant="ghost"
            size="sm"
            onClick={() => loadStores(secret)}
            disabled={loading}
            className="text-gray-400 hover:text-white"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            data-testid="admin-logout-btn"
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-gray-400 hover:text-white"
          >
            <LogOut className="h-4 w-4 mr-1" />
            Sign out
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Merchant Feature Flags</h2>
          <p className="text-sm text-gray-400 mt-1">Toggle capabilities per merchant without code deploys.</p>
        </div>

        {loading && stores.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
          </div>
        ) : stores.length === 0 ? (
          <p className="text-gray-500 text-sm py-8 text-center">No active stores found.</p>
        ) : (
          <div className="space-y-3">
            {stores.map((store, idx) => (
              <StoreRow
                key={`${store.shop_domain}-${idx}`}
                store={store}
                secret={secret}
                onFlagChange={handleFlagChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
